import React from 'react'
import {
  Alert,
  Badge,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'
import { Formik } from 'formik'
import ErrorBoundary from 'components/ErrorBoundary'
import FieldsetItemLarge from 'components/FieldsetItemLarge'
import FormikInputField from 'components/FormikInputField'
import SchoolLookUp from 'components/SchoolLookUp'
import { editSiteSchema } from 'lib/schemas'
import { Site, SchoolLookUpSiteData } from 'types'
import { CancelButton, SubmitButton } from './component.styles'

interface EditSiteModalProps {
  assignedTo: string
  companyName: string
  currentSchool: SchoolLookUpSiteData
  customerIdValidateAsync: (
    value: string,
    formik: any,
  ) => Promise<string | undefined>
  handleSubmit: (
    values: FormValues,
    helpers: { setSubmitting: (isSubmitting: boolean) => void },
  ) => Promise<void>
  isOpen: boolean
  schoolSelected: (formik: any, data: SchoolLookUpSiteData) => Promise<void>
  site: Site
  toggle: () => void
}

interface FormValues extends SchoolLookUpSiteData {
  id: number
  organizationId: number
}

const EditSiteModal: React.FC<EditSiteModalProps> = ({
  assignedTo,
  companyName,
  currentSchool,
  customerIdValidateAsync,
  handleSubmit,
  isOpen,
  schoolSelected,
  site,
  toggle,
}) => {
  return (
    <Modal
      backdrop="static"
      centered
      fade
      isOpen={isOpen}
      size="lg"
      toggle={toggle}
    >
      <ModalHeader toggle={toggle}>
        Site Edit&nbsp;
        <Badge>SS: {site.id}</Badge>
      </ModalHeader>
      <ErrorBoundary>
        <Formik<FormValues>
          enableReinitialize
          initialValues={{
            id: site.id,
            organizationId: site.organization.id,
            ...currentSchool,
          }}
          onSubmit={handleSubmit}
          validationSchema={editSiteSchema}
        >
          {formikProps => {
            // Check if form can be submitted
            // Allow submission if required fields are present and there are no errors
            const hasErrors = Object.keys(formikProps.errors || {}).length > 0
            const hasCountry = !!formikProps.values.country
            const hasOrganizationId = !!formikProps.values.organizationId
            // City is required only if nsCustomerId is empty
            const nsCustomerIdValue = formikProps.values.nsCustomerId
            const hasNsCustomerId =
              nsCustomerIdValue !== '' &&
              nsCustomerIdValue !== null &&
              nsCustomerIdValue !== undefined
            const hasCity =
              formikProps.values.city &&
              String(formikProps.values.city).trim() !== ''
            const hasCityOrNsCustomerId = hasNsCustomerId || hasCity

            const hasRequiredFields =
              hasCountry && hasOrganizationId && hasCityOrNsCustomerId
            const canSubmit =
              !formikProps.isSubmitting && hasRequiredFields && !hasErrors

            return (
              <form onSubmit={formikProps.handleSubmit}>
                <ModalBody>
                  <fieldset>
                    <legend>Details</legend>
                    <Row>
                      <Col sm={8}>
                        <FieldsetItemLarge
                          data={companyName || 'Not Assigned to NS Customer'}
                          title="Customer Name"
                        />
                        <FieldsetItemLarge
                          data={assignedTo}
                          title="Assigned To"
                        />
                      </Col>
                    </Row>
                  </fieldset>
                  <hr />
                  <h5>OrganizationId</h5>
                  <FormikInputField
                    label="Organization ID"
                    name="organizationId"
                    type="number"
                    {...formikProps}
                  />
                  <hr />
                  <h5>Assignee</h5>
                  <Alert color="info">
                    Reassign the Netsuite Customer that is linked to this site
                    by entering the NetSuite Customer ID OR by using the
                    dropdown menu. If you use the dropdown menu, leave the
                    Customer ID blank
                  </Alert>
                  <FormikInputField
                    label="NetSuite Customer ID"
                    name="nsCustomerId"
                    type="text"
                    validate={(value: string) =>
                      customerIdValidateAsync(value, formikProps)
                    }
                    {...formikProps}
                  />
                  <hr />
                  <SchoolLookUp
                    disabled={!!formikProps.values.nsCustomerId}
                    key={site.id}
                    onSelection={data => schoolSelected(formikProps, data)}
                    siteData={currentSchool}
                  />
                </ModalBody>
                <ModalFooter>
                  <CancelButton
                    className="btn-fixed-width"
                    color="link"
                    disabled={formikProps.isSubmitting}
                    onClick={toggle}
                  >
                    Cancel
                  </CancelButton>
                  <SubmitButton
                    className="btn-fixed-width"
                    color={formikProps.isSubmitting ? 'disabled' : 'primary'}
                    disabled={!canSubmit}
                    type="submit"
                  >
                    Submit
                  </SubmitButton>
                </ModalFooter>
              </form>
            )
          }}
        </Formik>
      </ErrorBoundary>
    </Modal>
  )
}

export default EditSiteModal
